import { FloatingTabContainer } from "@features/atoms/FloatingTabContainer"
import { TabControl } from "@features/atoms/TabControl"
import { SceneName } from "@features/gfx/generator/generator"
import { useBackground } from "@hooks/useAppState"
import { useAppData } from "eddev/hooks"
import { useRoute, useRouter } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { useWindowSize } from "react-use"
import { useEffect } from "react"

type Props = {}

export function PillarTabs(props: Props) {
  const router = useRouter()
  const route = useRoute()
  const pillars = useAppData((data) => data.pillars?.nodes ?? [])
  const [{ scene }, setBackground] = useBackground()
  const { width } = useWindowSize()

  const isMobile = width < 900
  const isPillarPage = route.view === "single-pillar"
  const isHomepage = route.view === "front-page"

  const showTabs = isPillarPage || isHomepage

  const rawPath = route?.pathname ?? router?.location?.pathname ?? ""
  const path = rawPath.replace(/\/+$/, "") || "/"

  useEffect(() => {
    // Only sync scene from URL on mobile when we're actually on a pillar page
    if (!isMobile || !isPillarPage) return
    const current = pillars.find((p) => (p.uri ?? "").replace(/\/+$/, "") === path)
    if (current && scene !== current.slug) {
      setBackground({ scene: current.slug as SceneName, darken: 0 })
    }
  }, [isMobile, isPillarPage, path, pillars, scene, setBackground])

  return (
    <FloatingTabContainer enabled={showTabs}>
      <TabControl
        items={pillars?.map((item) => ({
          label: (
            <AdaptiveLabel
              // no "Learn More" on mobile; still shows on desktop homepage for the active scene
              active={!isMobile && isHomepage && scene === item.slug}
              label={item.title!}
            />
          ),
          value: item.slug!,
          href: item.uri!,
          onClick: (e) => {
            e.preventDefault()
            // On mobile + homepage: stay here and just set the Scene
            if (isMobile && isHomepage) {
              setBackground({ scene: item.slug as SceneName, darken: 0 })
              return
            }
            // Otherwise navigate (desktop anywhere, or mobile when already on a pillar page)
            router.navigate(item.uri!, { linkData: {} })
          },
        }))}
        value={scene}
        onHoverItem={(value) =>
          // keep hover-only background preview for desktop homepage
          !isMobile && !isPillarPage &&
          setBackground({ scene: (value as SceneName) ?? "idle", darken: 0 })
        }
        classNames={{
          tab: "w-[20vw] px-0 md:w-[15em]",
        }}
      />
    </FloatingTabContainer>
  )
}

type LabelProps = {
  label: string
  active: boolean
}

function AdaptiveLabel({ label, active }: LabelProps) {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        key={active ? "active" : "inactive"}
        className="flex items-center justify-center gap-2 absolute inset-0"
      >
        {active ? <span>Learn More &rarr;</span> : <span>{label}</span>}
      </motion.div>
    </AnimatePresence>
  )
}
